import XIcon from "@assets/icons/x-circle.svg";

import useUserStore from "@store/useUserStore";
import useSystemStore from "@store/useSystemStore";
import { APP_COLORS } from "@styles/colors";
import { cn } from "@utils/tailwind.util";
import { isInactiveMember, isHighlightedMember } from "@utils/users.util";
import { User } from "@app-types/common.type";
import { LetterAvatar } from "@ui/Avatars";
import { Tooltip } from "@ui/Tooltip";

export interface IMemberListItemProps {
  member: User;
  erasable?: boolean;
  compactErasable?: boolean;
  onDelete?: () => void;
  isPending?: boolean;
  noPadding?: boolean;
  itemStyle?: "default" | "alternate";
  size?: "small" | "medium";
}

export function MemberListItem({
  member,
  erasable,
  compactErasable,
  onDelete,
  isPending = false,
  noPadding = false,
  itemStyle = "default",
  size = "small",
}: IMemberListItemProps) {
  const { activeUser } = useUserStore();
  const { isDarkMode } = useSystemStore();

  const selectedBackground = isDarkMode
    ? APP_COLORS.background.primaryDark
    : APP_COLORS.background.primary;
  const selectedText = isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary;

  const hasName = !!member?.first_name || !!member?.last_name;
  const hasTitle = member?.title !== null;

  const orgDomains = activeUser?.organization?.domains;
  const isInactive = isInactiveMember(member, orgDomains);
  const isHighlighted = isHighlightedMember(member, orgDomains);

  const getHoveredBackground = (): string => {
    switch (true) {
      case isDarkMode && !isHighlighted:
        return "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #313643) 50.41%)";
      case isDarkMode && isHighlighted:
        return "linear-gradient(90deg, rgba(56, 87, 83, 0.00) 0%, var(--tag-hover, #2A3C41) 50.41%)";
      case !isDarkMode && !isHighlighted:
        return "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #F3F4F6) 50.41%)";
      case !isDarkMode && isHighlighted:
        return "linear-gradient(90deg, rgba(138, 178, 189, 0.00) 0%, var(--tag-hover, #DCEBE9) 50.41%)";
    }
  };

  const getVisualStyles = () => {
    // Erasable item: hover effect
    if (erasable && !compactErasable) {
      return "min-h-[36px] hover:bg-background-clear";
    }

    // Alternate style: minimal padding
    if (itemStyle === "alternate") {
      return "w-fit p-0.5 pr-1";
    }

    // Active users and inactive users (internal or external with matching domain) - green
    if (isHighlighted) {
      return "w-fit rounded-md bg-teal-100 dark:bg-teal-950 p-0.5 pr-1";
    }

    // External users with non-matching domain - gray
    return "w-fit rounded-md bg-background-tag dark:bg-gray-750 p-0.5 pr-1";
  };

  const strokeColor = isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary;

  const customStyles = cn(
    "relative group flex w-full select-none items-center justify-between rounded-lg max-w-full",
    !noPadding && "px-2",
    getVisualStyles(),
    isHighlighted && {
      background: selectedBackground,
      color: selectedText,
      border: `1px solid ${strokeColor}`,
    }
  );

  return (
    <div
      className={customStyles}
      style={
        isHighlighted
          ? {
              background: selectedBackground,
              color: selectedText,
              border: `1px solid ${
                isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary
              }`,
            }
          : undefined
      }
    >
      <div className="flex items-center min-w-0">
        <div
          className={cn("shrink-0", {
            "mr-2": erasable,
            "mr-1": !erasable,
          })}
        >
          <LetterAvatar
            user={member}
            sizeClassName={cn({
              "w-5 h-5": erasable,
              "w-4 h-4 text-[10px] border-none mx-0":
                !erasable && itemStyle === "default" && size === "small",
              "w-5 h-5 text-[10px] border-none mx-0":
                !erasable && itemStyle === "alternate" && size === "small",
              "w-6 h-6 text-[12px] border-none mx-0": !erasable && size === "medium",
            })}
          />
        </div>
        {isInactive && (
          <Tooltip
            placement="top"
            variant="dark"
            tooltipContent={"Inactive member"}
            arrow={true}
            className="flex w-fit"
          >
            <span
              className={cn(
                "mr-1 block truncate max-w-full text-sm text-content-secondary dark:text-invertedPrimary opacity-40",
                {
                  "text-xs": !erasable && size === "small",
                  "text-sm !leading-normal":
                    !erasable && itemStyle === "alternate" && size === "small",
                  "text-sm": !erasable && size === "medium",
                }
              )}
            >
              {member.email}
            </span>
          </Tooltip>
        )}
        {isPending && !isInactive && (
          <Tooltip placement="top" arrow tooltipContent={<p className="text-sm">Pending user</p>}>
            <span
              className={cn(
                "mr-1 block truncate max-w-full text-sm text-content-secondary dark:text-invertedPrimary",
                {
                  "text-xs": !erasable && size === "small",
                  "text-sm !leading-normal":
                    !erasable && itemStyle === "alternate" && size === "small",
                  "text-sm": !erasable && size === "medium",
                }
              )}
            >
              {member.email}
            </span>
          </Tooltip>
        )}
        {!isPending && !hasName && !isInactive && (
          <span
            className={cn(
              "mr-1 block truncate max-w-full text-sm font-medium text-content-primary dark:text-invertedPrimary",
              {
                "text-xs": !erasable && size === "small",
                "text-sm !leading-normal":
                  !erasable && itemStyle === "alternate" && size === "small",
                "text-sm": !erasable && size === "medium",
              }
            )}
          >
            {member.email}
          </span>
        )}
        {!isPending && hasName && !isInactive && (
          <span
            className={cn(
              "mr-1 block truncate max-w-full text-sm font-medium text-content-primary dark:text-invertedPrimary",
              {
                "text-xs": !erasable && size === "small",
                "text-sm !leading-normal":
                  !erasable && itemStyle === "alternate" && size === "small",
                "text-sm": !erasable && size === "medium",
              }
            )}
          >
            {member.first_name} {member.last_name}
          </span>
        )}
        {!isPending && hasTitle && !isInactive && (
          <span
            className={cn(
              "min-w-10 truncate text-overflow-ellipsis max-w-full text-sm font-medium text-content-secondary dark:text-invertedPrimary80",
              {
                "text-xs": !erasable && size === "small",
                "text-sm !leading-normal":
                  !erasable && itemStyle === "alternate" && size === "small",
                "text-sm": !erasable && size === "medium",
              }
            )}
          >
            {member?.title?.title || ""}
          </span>
        )}
      </div>
      {erasable && (
        <>
          <XIcon
            width={20}
            height={20}
            className={cn(
              "absolute bottom-0 right-1 top-0 h-full z-20 invisible flex-shrink-0 cursor-pointer p-0 pr-1 text-gray-450 dark:text-invertedPrimary80 group-hover:visible",
              {
                "flex w-4 items-center justify-end rounded p-0 leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100":
                  compactErasable,
              }
            )}
            onClick={onDelete}
          />

          <div
            className="pointer-events-none absolute right-0 top-0 z-10 h-full w-14 rounded opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
            style={{ background: getHoveredBackground() }}
          />
        </>
      )}
    </div>
  );
}
